import { useRef, useCallback, useEffect } from "react";
import { track } from "../../analytics/AnalyticsService.js";
import { AnalyticsTrackingEvent } from "../../analytics/events.js";
import { tryGetVisitorId } from "../../analytics/fingerprint.js";
import { getPageNameFromPath, getUTMParams, getDeviceType } from "../tracking/ConciergeTrackingTypes.js";
const SESSION_TRACKED_KEY = "concierge_session_tracked";
function useWidgetVisibilityTracking(options = {}) {
  const { appName = "", appConfigId, widgetType = "floating_button", enabled = true, pageKey } = options;
  const hasTrackedImpression = useRef(false);
  const observerRef = useRef(null);
  const elementRef = useRef(null);
  const isSessionTracked = useCallback(() => {
    if (typeof window === "undefined") return true;
    try {
      return sessionStorage.getItem(SESSION_TRACKED_KEY) === "true";
    } catch {
      return false;
    }
  }, []);
  const markSessionTracked = useCallback(() => {
    if (typeof window === "undefined") return;
    try {
      sessionStorage.setItem(SESSION_TRACKED_KEY, "true");
    } catch {
    }
  }, []);
  const trackWidgetImpression = useCallback(async () => {
    if (!enabled || hasTrackedImpression.current) return;
    hasTrackedImpression.current = true;
    const sessionId = await tryGetVisitorId() || "unknown";
    const properties = {
      pageUrl: window.location.href,
      pageName: getPageNameFromPath(window.location.pathname),
      widgetType,
      sessionId,
      appName: appName || "unknown",
      ...appConfigId && { appConfigId }
    };
    track(AnalyticsTrackingEvent.CONCIERGE_WIDGET_IMPRESSION, properties).catch((err) => {
      console.debug("[Concierge Tracking] Failed to track widget impression:", err);
    });
  }, [enabled, widgetType, appName, appConfigId]);
  const trackWidgetSessionStart = useCallback(async () => {
    if (!enabled || isSessionTracked()) return;
    markSessionTracked();
    const sessionId = await tryGetVisitorId() || "unknown";
    const utmParams = getUTMParams();
    const properties = {
      sessionId,
      appName: appName || "unknown",
      referrer: document.referrer || "",
      deviceType: getDeviceType(),
      pageUrl: window.location.href,
      ...utmParams
    };
    track(AnalyticsTrackingEvent.CONCIERGE_WIDGET_SESSION_START, properties).catch((err) => {
      console.debug("[Concierge Tracking] Failed to track widget session start:", err);
    });
  }, [enabled, appName, isSessionTracked, markSessionTracked]);
  useEffect(() => {
    if (!enabled || typeof window === "undefined") return;
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
    observerRef.current = new IntersectionObserver(
      (entries) => {
        var _a;
        const entry = entries[0];
        if (entry == null ? void 0 : entry.isIntersecting) {
          trackWidgetImpression();
          trackWidgetSessionStart();
          (_a = observerRef.current) == null ? void 0 : _a.disconnect();
        }
      },
      {
        // Fire when at least 50% of the element is visible
        threshold: 0.5
      }
    );
    if (elementRef.current) {
      observerRef.current.observe(elementRef.current);
    }
    return () => {
      var _a;
      (_a = observerRef.current) == null ? void 0 : _a.disconnect();
    };
  }, [enabled, trackWidgetImpression, trackWidgetSessionStart]);
  const pageKeyRef = useRef(pageKey);
  useEffect(() => {
    if (!enabled || pageKey === void 0 || pageKey === pageKeyRef.current) return;
    pageKeyRef.current = pageKey;
    hasTrackedImpression.current = false;
    trackWidgetImpression();
  }, [pageKey, enabled, trackWidgetImpression]);
  const setTrackingRef = useCallback((element) => {
    elementRef.current = element;
    if (element && observerRef.current) {
      observerRef.current.observe(element);
    }
  }, []);
  const trackImmediate = useCallback(() => {
    trackWidgetImpression();
    trackWidgetSessionStart();
  }, [trackWidgetImpression, trackWidgetSessionStart]);
  return {
    /** Ref callback to attach to the widget element for visibility-based tracking */
    setTrackingRef,
    /** Manually trigger tracking immediately (bypasses IntersectionObserver) */
    trackImmediate
  };
}
export {
  useWidgetVisibilityTracking
};
//# sourceMappingURL=useWidgetVisibilityTracking.js.map
